import { useEffect, useRef, useState } from 'react';
import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import { FiArrowUpRight, FiSearch, FiUser, FiX } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import navigationItems from './navigationItems';

const pages = [...navigationItems, { path: '/profile', name: 'Profile', icon: FiUser }];

const QuickNavigation = ({ open, onClose }) => {
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const search = query.trim().toLocaleLowerCase();
  const filteredPages = pages.filter((page) => page.name.toLocaleLowerCase().includes(search));

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const openPage = (page) => {
    if (!page) return;
    navigate(page.path);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      initialFocus={inputRef}
      transition
      className="relative z-[70] transition duration-150 data-[closed]:opacity-0 motion-reduce:transition-none"
    >
      <DialogBackdrop
        transition
        onClick={() => onClose()}
        data-testid="quick-navigation-backdrop"
        className="fixed inset-0 bg-slate-950/40 backdrop-blur-sm transition-opacity duration-150 data-[closed]:opacity-0 motion-reduce:transition-none"
      />

      {/* DialogPanel stops clicks inside; close the entire palette on outside clicks. */}
      <div role="presentation" onClick={() => onClose()} className="fixed inset-0 overflow-y-auto p-3 sm:p-6">
        <div className="mx-auto flex min-h-full max-w-xl items-start justify-center pt-[8dvh] sm:pt-[12dvh]">
          <DialogPanel
            transition
            onKeyDownCapture={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                onClose();
              }
            }}
            className="w-full overflow-hidden rounded-2xl border border-white/80 bg-white shadow-[0_24px_90px_rgba(15,23,42,0.25)] transition duration-150 data-[closed]:translate-y-2 data-[closed]:opacity-0 motion-reduce:transform-none motion-reduce:transition-none sm:rounded-3xl"
          >
            <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
              <div>
                <DialogTitle className="text-base font-semibold text-slate-900">Quick navigation</DialogTitle>
                <p className="mt-0.5 text-xs text-slate-500">Find your next admin page.</p>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close quick navigation"
                className="rounded-xl p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500"
              >
                <FiX className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>

            <Combobox value={null} onChange={openPage} immediate>
              <div className="relative m-4 sm:m-5">
                <FiSearch className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-amber-600" aria-hidden="true" />
                <ComboboxInput
                  ref={inputRef}
                  autoFocus
                  aria-label="Search admin pages"
                  placeholder="Search admin pages…"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDownCapture={(event) => {
                    // Tab moves between dialog controls without opening the highlighted page.
                    if (event.key === 'Tab') event.stopPropagation();
                  }}
                  autoComplete="off"
                  className="w-full rounded-xl border border-slate-200 bg-slate-50/70 py-3.5 pl-12 pr-4 text-sm text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-amber-400 focus:bg-white focus:ring-4 focus:ring-amber-100 motion-reduce:transition-none"
                />
              </div>

              {filteredPages.length > 0 ? (
                <>
                  <p className="px-5 pb-2 text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 sm:px-6">
                    {search ? 'Matching pages' : 'Workspace pages'}
                  </p>
                  <ComboboxOptions
                    static
                    modal={false}
                    as="ul"
                    aria-label="Admin pages"
                    className="max-h-[min(23rem,45dvh)] space-y-1 overflow-y-auto px-3 pb-4 sm:px-4"
                  >
                    {filteredPages.map((page) => {
                      const current = page.path === '/'
                        ? pathname === '/'
                        : pathname === page.path || pathname.startsWith(`${page.path}/`);
                      const Icon = page.icon;

                      return (
                        <ComboboxOption
                          key={page.path}
                          value={page}
                          as="li"
                          className="group flex cursor-pointer items-center gap-3 rounded-xl px-3 py-3 text-slate-700 transition-colors data-[focus]:bg-amber-50 data-[focus]:text-slate-900 motion-reduce:transition-none"
                        >
                          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-amber-100 bg-amber-50/70 text-amber-600 group-data-[focus]:border-amber-200 group-data-[focus]:bg-white">
                            <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-semibold">{page.name}</span>
                            <span className="mt-0.5 block text-xs text-slate-400">Open page</span>
                          </span>
                          {current && (
                            <span className="shrink-0 rounded-full bg-slate-100 px-2 py-1 text-[10px] font-medium text-slate-500">Current</span>
                          )}
                          <FiArrowUpRight className="h-4 w-4 shrink-0 text-slate-300 group-data-[focus]:text-amber-600" aria-hidden="true" />
                        </ComboboxOption>
                      );
                    })}
                  </ComboboxOptions>
                </>
              ) : (
                <div className="px-6 py-10 text-center" role="status">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50 text-slate-400">
                    <FiSearch className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <p className="mt-3 text-sm font-medium text-slate-900">No pages found</p>
                  <p className="mt-1 text-xs text-slate-500">Try a page name like Users, Support, or Promo Codes.</p>
                </div>
              )}
            </Combobox>

            <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/70 px-5 py-3 text-[11px] text-slate-400 sm:px-6">
              <span><span aria-hidden="true">↑ ↓</span> to navigate · Enter to open</span>
              <span className="shrink-0">Esc to close</span>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default QuickNavigation;
